import React from 'react';
import moment from 'moment';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown, Heart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AccordionItem, AccordionContent } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CardProgressWrapper } from '@/components/ui/card';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import { LessonPlanSmartLessonCard } from './lesson-plan-smart-lesson-card';
import { QUARTER_LABEL } from './lesson-plan-smart-constants';

// Mesmo gradient usado nos cards do tema playful (PlayfulCard, lesson-plan.jsx).
const PLAYFUL_CARD_GRADIENT = 'linear-gradient(180deg, #F4FDFF 0%, #F2FCFF 14.65%, #FFFCF1 27.71%)';

function formatScheduleTime(startTime, endTime) {
  if (!startTime && !endTime) return null;
  const format = time => (moment(time, 'HH:mm').minutes() === 0 ? moment(time, 'HH:mm').format('H[h]') : moment(time, 'HH:mm').format('H[h]mm'));
  if (startTime && endTime) return `das ${format(startTime)} às ${format(endTime)}`;
  return `às ${format(startTime || endTime)}`;
}

function formatScheduleLine(schedule) {
  const time = formatScheduleTime(schedule.startTime, schedule.endTime);
  const parts = [schedule.weekday, time, schedule.teacherName && `Prof. ${schedule.teacherName}`].filter(Boolean);
  return parts.join(', ');
}

export function LessonPlanSmartSubject({ subject, isOpen, lessonsState, onToggleFavorite }) {
  const playfulTeacher = usePlayfulTeacher();
  const progress = subject.progressPercentage ?? 0;
  const playfulOpen = playfulTeacher && isOpen;

  const periodRange = subject.periodStartDate && subject.periodEndDate ? `${moment(subject.periodStartDate).format('DD/MM/YYYY')} a ${moment(subject.periodEndDate).format('DD/MM/YYYY')}` : null;
  const quarterLabel = QUARTER_LABEL[subject.quarter] || subject.quarter;

  return (
    <AccordionItem
      value={subject.id}
      className={cn('rounded-2xl border-2 bg-white p-6 shadow-none transition-colors', playfulTeacher ? 'border-neutral-lightest data-[state=open]:border-[var(--color-secondary)]' : 'border-primary-lightest data-[state=open]:border-primary-light')}
      style={playfulOpen ? { background: PLAYFUL_CARD_GRADIENT } : undefined}
    >
      <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger asChild>
          <div
            role="button"
            tabIndex={0}
            className="flex w-full cursor-pointer flex-col gap-2 [&[data-state=open]_svg.c-lesson-plan-smart-chevron]:rotate-180"
          >
            <div className="flex items-center justify-between gap-4">
              <h3 className={cn('mb-0 text-lg font-bold', playfulTeacher ? 'text-neutral-900' : 'text-primary-dark')}>{subject.title}</h3>

              <div className="flex shrink-0 items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!subject.file}
                  className={playfulTeacher ? 'border-none bg-transparent text-white hover:border-none hover:bg-transparent hover:text-white' : undefined}
                  style={playfulTeacher ? { background: 'var(--gradient-btn-continue)', boxShadow: '0 3px 0 var(--color-mission-dark)' } : undefined}
                  onClick={event => {
                    event.stopPropagation();
                    if (subject.file) window.open(subject.file, '_blank', 'noopener,noreferrer');
                  }}
                >
                  Baixar PDF
                </Button>

                <button
                  type="button"
                  aria-label={subject.favorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                  onClick={event => {
                    event.stopPropagation();
                    onToggleFavorite();
                  }}
                >
                  <Heart className={cn('size-6', subject.favorited ? 'fill-danger text-danger' : 'text-neutral')} />
                </button>

                <ChevronDown className="c-lesson-plan-smart-chevron size-6 shrink-0 text-primary-dark transition-transform duration-200" />
              </div>
            </div>

            <div className="flex w-full flex-col gap-1">
              <p className={cn('mb-0 text-base', playfulTeacher ? 'font-semibold text-neutral-500' : 'font-medium text-black')}>
                Aula {subject.currentLessonNumber} de {subject.lessonsTotalCount} · {subject.lessonsCompletedCount} realizadas
              </p>
              {playfulTeacher ? (
                <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--color-bg-lilac)]">
                  <div
                    className="h-full rounded-full transition-[width] delay-300 duration-700 ease-out-strong"
                    style={{ width: `${Math.min(progress, 100)}%`, background: 'var(--gradient-progress-bar)' }}
                  />
                </div>
              ) : (
                <CardProgressWrapper percentage={progress} />
              )}
            </div>
          </div>
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>

      <AccordionContent className="mx-0 mt-5 border-t-0 px-0 py-0">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-5 md:flex-row">
            <div className={cn('flex flex-1 flex-col gap-4 rounded-2xl p-4', playfulTeacher ? 'border border-neutral-lightest bg-white' : 'bg-neutral-bright')}>
              <p className={cn('mb-0 border-b border-neutral-lighter pb-1.5 text-sm font-bold', playfulTeacher ? 'text-neutral-500' : 'text-neutral-dark')}>COMPONENTE CURRICULAR</p>
              <div className={cn('flex flex-col gap-1', playfulTeacher ? 'text-neutral-900' : 'text-primary-dark')}>
                <p className="mb-0 text-base font-bold">{subject.componentName}</p>
                {subject.schedules?.map((schedule, index) => (
                  <p
                    key={`${schedule.weekday}-${index}`}
                    className="mb-0 text-sm"
                  >
                    {formatScheduleLine(schedule)}
                  </p>
                ))}
                <p className="mb-0 text-sm">
                  Período: {periodRange} · Turno: {subject.shift}
                </p>
              </div>
            </div>

            <div className={cn('flex flex-1 flex-col gap-4 rounded-2xl p-4', playfulTeacher ? 'border border-neutral-lightest bg-white' : 'bg-neutral-bright')}>
              <p className={cn('mb-0 border-b border-neutral-lighter pb-1.5 text-sm font-bold uppercase', playfulTeacher ? 'text-neutral-500' : 'text-neutral-dark')}>Habilidades da BNCC</p>
              <div className="flex flex-col gap-3">
                {subject.skills.map(skill => (
                  <div
                    key={skill.code}
                    className="flex items-start gap-2.5"
                  >
                    {playfulTeacher ? (
                      <Badge
                        variant="amber-soft"
                        size="sm"
                        className="shrink-0"
                      >
                        {skill.code}
                      </Badge>
                    ) : (
                      <span className="inline-block shrink-0 truncate rounded-full bg-secondary-dark px-2 py-0.5 text-xs font-bold text-white">{skill.code}</span>
                    )}
                    <p className={cn('mb-0 flex-1 text-sm', playfulTeacher ? 'text-neutral-900' : 'text-primary-dark')}>{skill.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={cn('flex flex-col gap-4 rounded-2xl p-4', playfulTeacher ? 'border border-neutral-lightest bg-white' : 'bg-neutral-bright')}>
            <p className={cn('mb-0 border-b border-neutral-lighter pb-1.5 text-sm font-bold', playfulTeacher ? 'text-neutral-500' : 'text-neutral-dark')}>
              AULAS DO {quarterLabel?.toUpperCase()} {periodRange ? `| ${periodRange}` : ''}
            </p>

            {lessonsState?.isLoading ? (
              <Loader />
            ) : lessonsState?.error ? (
              <EmptyState
                type="disapproved"
                text={lessonsState.error}
                variant="plain"
              />
            ) : lessonsState?.months?.length > 0 ? (
              lessonsState.months.map(month => (
                <div
                  key={month.name}
                  className="flex flex-col gap-1"
                >
                  <p className={cn('mb-0 text-base font-bold', playfulTeacher ? 'text-neutral-900' : 'text-black')}>{month.name}</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                    {month.lessons.map(lesson => (
                      <LessonPlanSmartLessonCard
                        key={lesson.id}
                        lesson={lesson}
                        subject={subject}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <EmptyState
                type="data"
                text="Sem aulas cadastradas para este plano."
                variant="plain"
              />
            )}
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
